/**
 * RTM listener for outbound:failed events from the desk socket.
 * When an agent sends a WhatsApp message via the New Message modal, delivery can fail
 * asynchronously. This hook surfaces that failure as an error toast with a translated
 * description mapped from the WhatsApp status code.
 */
import { useCallback, useContext, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { SocketContext } from '@/contexts/rtm';
import { selectUserId } from '@/redux/user/selectors';
import { OutboundFailedPayload, ServerToClientEvents, SocketEvent } from '@/shared/types/rtm';

/**
 * Returns a translated description for the given status code.
 * Looks up error_codes.{statusCode} first, falls back to a generic message.
 */
const getErrorDescription = (
  t: (key: string, options?: Record<string, string>) => string,
  statusCode?: string
): string => {
  if (!statusCode) {
    return t('conversation.outbound_failed.unknown');
  }

  return t(`error_codes.${statusCode}`, {
    defaultValue: t('conversation.outbound_failed.default', { '0': statusCode }),
  });
};

/**
 * Subscribes to outbound:failed and shows an error toast to the agent who sent the message.
 */
export const useRtmOutboundFailed = () => {
  const { socket } = useContext(SocketContext);
  const userId = useSelector(selectUserId);
  const { t } = useTranslation();

  const handleOutboundFailed: ServerToClientEvents[SocketEvent.outbound_failed] = useCallback(
    ({ data }: { data: OutboundFailedPayload }) => {
      // Only show the toast to the agent who initiated the outbound message
      if (data.initiator_user_id !== userId) {
        return;
      }

      const description = getErrorDescription(t, data.status_code);
      toast.error(t('conversation.outbound_failed.title'), { description });
    },
    [t, userId]
  );

  useEffect(() => {
    socket?.on(SocketEvent.outbound_failed, handleOutboundFailed);
    return () => {
      socket?.off(SocketEvent.outbound_failed, handleOutboundFailed);
    };
  }, [handleOutboundFailed, socket]);
};
